/**
 * Central mapping from backend error codes to i18n keys.
 *
 * The Go backend's `writeErrorCode` helper emits snake_case codes (see
 * server/internal/httpapi/routes/helpers.go). Each code has a corresponding
 * entry here that resolves to a key in i18n/messages/<locale>/errors.json.
 *
 * Adding a new code: pick a snake_case identifier that describes the error
 * class (not the endpoint), add it to ERROR_CODE_TO_I18N, and add the matching
 * translation under `errors.*` in every locale file. CI enforces locale parity
 * via the check-translations workflow.
 *
 * Unknown codes fall back to `errors.generic` — callers never need to guard
 * against a missing translation.
 */

export const ERROR_CODE_FALLBACK = 'errors.api.generic' as const;

/**
 * Exhaustive mapping. Keep sorted alphabetically within each group and grouped
 * by feature for readability. All keys live under `errors.api.*` to avoid
 * colliding with the pre-existing structured entries (errors.validation,
 * errors.notFound, etc.).
 */
export const ERROR_CODE_TO_I18N: Record<string, string> = {
  // Generic / HTTP fallbacks
  auth_required: 'errors.api.authRequired',
  forbidden: 'errors.api.forbidden',
  not_found: 'errors.api.notFound',
  conflict: 'errors.api.conflict',
  rate_limited: 'errors.api.rateLimited',
  // §11 — workspace / org / ip layered rate-limiter. Distinct from
  // rate_limited (single bucket) because the response body carries
  // scope + bucketScope fields the UI can surface in the toast.
  rate_limit_exceeded: 'errors.api.rateLimited',
  // §11 — per-org override validation. Bounds for chat / projects
  // are checked at the admin PATCH layer; the codes are namespaced
  // per scope so the UI can highlight the right field.
  chat_max_requests_out_of_range: 'errors.api.invalidParam',
  chat_window_ms_out_of_range: 'errors.api.invalidParam',
  projects_max_requests_out_of_range: 'errors.api.invalidParam',
  projects_window_ms_out_of_range: 'errors.api.invalidParam',
  // ER §1 — per-org IP allowlist. ip_blocked is the runtime
  // rejection (403); ip_allowlist_unavailable is the fail-CLOSED
  // path when the DB lookup fails (503); the *_already_added /
  // entry_not_found / invalid_cidr surface on the admin POST/DELETE.
  ip_blocked: 'errors.api.ipBlocked',
  ip_allowlist_unavailable: 'errors.api.serviceUnavailable',
  ip_allowlist_cidr_already_added: 'errors.api.conflict',
  ip_allowlist_entry_not_found: 'errors.api.notFound',
  invalid_cidr: 'errors.api.invalidParam',
  invalid_description: 'errors.api.invalidParam',
  internal_error: 'errors.api.internalError',
  service_unavailable: 'errors.api.serviceUnavailable',
  database_unavailable: 'errors.api.databaseUnavailable',
  network_error: 'errors.api.networkError',

  // Request validation
  invalid_body: 'errors.api.invalidBody',
  invalid_param: 'errors.api.invalidParam',
  missing_field: 'errors.api.missingField',
  invalid_email: 'errors.api.invalidEmail',
  invalid_url: 'errors.api.invalidUrl',
  invalid_id: 'errors.api.invalidId',
  invalid_token: 'errors.api.invalidToken',
  invalid_code: 'errors.api.invalidCode',
  invalid_state: 'errors.api.invalidState',
  invalid_signature: 'errors.api.invalidSignature',
  invalid_config: 'errors.api.invalidConfig',
  payload_too_large: 'errors.api.payloadTooLarge',
  unsupported_format: 'errors.api.unsupportedFormat',

  // Auth / sessions
  invalid_credentials: 'errors.api.invalidCredentials',
  email_taken: 'errors.api.emailTaken',
  weak_password: 'errors.api.weakPassword',
  email_not_verified: 'errors.api.emailNotVerified',
  account_disabled: 'errors.api.accountDisabled',
  account_locked: 'errors.api.accountLocked',
  too_many_attempts: 'errors.api.tooManyAttempts',
  session_expired: 'errors.api.sessionExpired',
  two_factor_required: 'errors.api.twoFactorRequired',
  two_factor_invalid: 'errors.api.twoFactorInvalid',
  challenge_expired: 'errors.api.challengeExpired',
  challenge_mismatch: 'errors.api.challengeMismatch',
  recovery_token_invalid: 'errors.api.recoveryTokenInvalid',
  password_reset_invalid: 'errors.api.passwordResetInvalid',
  set_password_invalid: 'errors.api.setPasswordInvalid',
  email_confirm_invalid: 'errors.api.emailConfirmInvalid',
  email_confirm_conflict: 'errors.api.emailConfirmConflict',
  sso_required: 'errors.api.ssoRequired',
  social_sign_in_failed: 'errors.api.socialSignInFailed',
  social_provider_not_configured: 'errors.api.socialProviderNotConfigured',
  disposable_email_blocked: 'errors.api.disposableEmailBlocked',
  unable_to_sign_in: 'errors.api.unableToSignIn',

  // Passkeys / WebAuthn — reuse existing auth.passkey.* keys
  passkey_sso_only: 'auth.passkey.ssoOnlyForAdmins',
  passkey_forbidden: 'auth.passkey.forbiddenForAccount',
  passkey_too_many: 'auth.passkey.tooManyCredentials',
  passkey_recovery_consumed: 'auth.passkey.recoveryLinkInvalid',
  passkey_hardware_required: 'auth.passkey.hardwareRequired',
  passkey_hardware_mds_unavailable: 'auth.passkey.hardwareMdsUnavailable',
  passkey_last_protected: 'auth.passkey.lastCredentialProtected',
  passkey_admin_required: 'auth.passkey.adminRequiresPasskey',
  passkey_clone_detected: 'auth.passkey.cloneDetected',
  no_passkey: 'auth.passkey.noPasskey',
  uv_reauth_required: 'auth.passkey.uvReauthRequired',
  invalid_registration: 'auth.passkey.registrationFailed',
  invalid_assertion: 'auth.passkey.loginFailed',
  credential_already_registered: 'auth.passkey.alreadyRegistered',

  // Passkey admin dual-control / org policy changes
  invalid_org: 'errors.api.invalidOrg',
  org_membership_required: 'errors.api.orgMembershipRequired',
  no_policy_change_requested: 'errors.api.noPolicyChangeRequested',
  invalid_change_id: 'errors.api.invalidChangeId',
  self_approval_forbidden: 'errors.api.selfApprovalForbidden',
  approval_failed: 'errors.api.approvalFailed',

  // M4 — Step-up authentication / Sudo Mode. The 412 envelope reuses
  // these codes; SudoChallengeModal short-circuits the global error
  // toast on these because the modal handles them inline.
  sudo_mode_required: 'auth.sudoMode.errors.invalidCredentials',
  sudo_mode_locked: 'auth.sudoMode.errors.locked',

  // Projects / workspaces
  project_not_found: 'errors.api.projectNotFound',
  workspace_not_found: 'errors.api.workspaceNotFound',
  workspace_member_not_found: 'errors.api.workspaceMemberNotFound',
  workspace_member_limit_reached: 'errors.api.workspaceMemberLimitReached',
  project_visibility_invalid: 'errors.api.projectVisibilityInvalid',
  project_archive_denied: 'errors.api.projectArchiveDenied',
  project_delete_denied: 'errors.api.projectDeleteDenied',
  folder_not_found: 'errors.api.folderNotFound',
  folder_cycle: 'errors.api.folderCycle',

  // Invites / sharing
  invite_not_found: 'errors.api.inviteNotFound',
  invite_expired: 'errors.api.inviteExpired',
  invite_revoked: 'errors.api.inviteRevoked',
  invite_accepted: 'errors.api.inviteAccepted',
  invite_declined: 'errors.api.inviteDeclined',
  invite_email_mismatch: 'errors.api.inviteEmailMismatch',
  invite_already_member: 'errors.api.inviteAlreadyMember',
  invite_email_send_failed: 'errors.api.inviteEmailSendFailed',

  // Billing / plans
  billing_plan_not_found: 'errors.api.billingPlanNotFound',
  billing_subscription_required: 'errors.api.billingSubscriptionRequired',
  billing_subscription_expired: 'errors.api.billingSubscriptionExpired',
  billing_payment_method_required: 'errors.api.billingPaymentMethodRequired',
  billing_stripe_error: 'errors.api.billingStripeError',
  quota_exceeded: 'errors.api.quotaExceeded',
  credits_exhausted: 'errors.api.creditsExhausted',

  // AI / chat
  ai_provider_not_configured: 'errors.api.aiProviderNotConfigured',
  ai_provider_unavailable: 'errors.api.aiProviderUnavailable',
  ai_provider_error: 'errors.api.aiProviderError',
  ai_quota_exceeded: 'errors.api.aiQuotaExceeded',
  chat_session_not_found: 'errors.api.chatSessionNotFound',
  chat_message_invalid: 'errors.api.chatMessageInvalid',

  // Internal server-to-server Next.js routes (/api/internal/*)
  internal_project_name_failed: 'errors.api.internalProjectNameFailed',
  internal_prompt_intake_failed: 'errors.api.internalPromptIntakeFailed',
  internal_ai_execute_failed: 'errors.api.internalAiExecuteFailed',
  model_unavailable: 'errors.api.modelUnavailable',

  // Admin / SCIM / SSO
  admin_required: 'errors.api.adminRequired',
  sso_tenant_not_found: 'errors.api.ssoTenantNotFound',
  sso_domain_conflict: 'errors.api.ssoDomainConflict',
  scim_token_invalid: 'errors.api.scimTokenInvalid',
  scim_resource_not_found: 'errors.api.scimResourceNotFound',

  // Connectors (Supabase/GitHub/Figma/etc.)
  connector_not_configured: 'errors.api.connectorNotConfigured',
  connector_oauth_failed: 'errors.api.connectorOAuthFailed',
  connector_token_revoked: 'errors.api.connectorTokenRevoked',
  connector_permission_denied: 'errors.api.connectorPermissionDenied',

  // Knowledge / uploads
  knowledge_not_found: 'errors.api.knowledgeNotFound',
  knowledge_size_limit: 'errors.api.knowledgeSizeLimit',
  upload_failed: 'errors.api.uploadFailed',

  // Misc
  resource_locked: 'errors.api.resourceLocked',
  feature_disabled: 'errors.api.featureDisabled',
  maintenance_mode: 'errors.api.maintenanceMode',

  // Announcements / admin integrations (added 2026-04-23)
  announcement_create_failed: 'errors.api.announcementCreateFailed',
  announcement_delete_failed: 'errors.api.announcementDeleteFailed',
  announcement_not_found: 'errors.api.announcementNotFound',
  announcement_update_failed: 'errors.api.announcementUpdateFailed',
  announcements_fetch_failed: 'errors.api.announcementsFetchFailed',
  announcements_reorder_failed: 'errors.api.announcementsReorderFailed',
  integration_fetch_failed: 'errors.api.integrationFetchFailed',
  integration_not_found: 'errors.api.integrationNotFound',
  integration_update_failed: 'errors.api.integrationUpdateFailed',
  mintlify_preview_failed: 'errors.api.mintlifyPreviewFailed',
  mintlify_sync_failed: 'errors.api.mintlifySyncFailed',
  mintlify_sync_running: 'errors.api.mintlifySyncRunning',
  mintlify_unavailable: 'errors.api.mintlifyUnavailable',

  // Preview-iframe dispatcher errors
  preview_error: 'errors.api.previewError',
  preview_not_found: 'errors.api.previewNotFound',
  preview_not_running: 'errors.api.previewNotRunning',
  preview_port_invalid: 'errors.api.previewPortInvalid',
  unauthorized: 'errors.api.previewUnauthorized',
  no_access: 'errors.api.previewNoAccess',

  // Assets / uploads (added 2026-04-23)
  asset_load_failed: 'errors.api.assetLoadFailed',
  asset_not_found: 'errors.api.assetNotFound',
  cross_project_sharing_disabled: 'errors.api.crossProjectSharingDisabled',
  invalid_asset_filename: 'errors.api.invalidAssetFilename',
  logo_invalid_base64: 'errors.api.logoInvalidBase64',
  logo_not_png: 'errors.api.logoNotPng',
  logo_save_failed: 'errors.api.logoSaveFailed',
  upload_empty: 'errors.api.uploadEmpty',
  upload_invalid_multipart: 'errors.api.uploadInvalidMultipart',
  upload_not_image: 'errors.api.uploadNotImage',
  upload_read_failed: 'errors.api.uploadReadFailed',
  upload_svg_blocked: 'errors.api.uploadSvgBlocked',

  // Billing (granular codes, added 2026-04-23)
  billing_checkout_failed: 'errors.api.billingCheckoutFailed',
  billing_enterprise_manual: 'errors.api.billingEnterpriseManual',
  billing_invalid_interval: 'errors.api.billingInvalidInterval',
  billing_invalid_plan: 'errors.api.billingInvalidPlan',
  billing_no_customer: 'errors.api.billingNoCustomer',
  billing_portal_failed: 'errors.api.billingPortalFailed',
  billing_price_not_configured: 'errors.api.billingPriceNotConfigured',
  billing_service_unavailable: 'errors.api.billingServiceUnavailable',
  billing_snapshot_failed: 'errors.api.billingSnapshotFailed',
  billing_stripe_not_configured: 'errors.api.billingStripeNotConfigured',
  billing_unknown_credit_pack: 'errors.api.billingUnknownCreditPack',
  billing_webhook_failed: 'errors.api.billingWebhookFailed',
  billing_webhook_invalid_payload: 'errors.api.billingWebhookInvalidPayload',
  billing_webhook_missing_signature: 'errors.api.billingWebhookMissingSignature',
  billing_webhook_not_configured: 'errors.api.billingWebhookNotConfigured',

  // Comments (added 2026-04-23)
  comment_create_failed: 'errors.api.commentCreateFailed',
  comment_delete_failed: 'errors.api.commentDeleteFailed',
  comment_load_failed: 'errors.api.commentLoadFailed',
  comment_not_found: 'errors.api.commentNotFound',
  comment_resolve_failed: 'errors.api.commentResolveFailed',
  comment_screenshot_failed: 'errors.api.commentScreenshotFailed',
  comment_too_long: 'errors.api.commentTooLong',
  comment_unresolve_failed: 'errors.api.commentUnresolveFailed',
  comment_update_failed: 'errors.api.commentUpdateFailed',
  comments_load_failed: 'errors.api.commentsLoadFailed',
  comments_mark_read_failed: 'errors.api.commentsMarkReadFailed',
  comments_unread_count_failed: 'errors.api.commentsUnreadCountFailed',
  invalid_emoji: 'errors.api.invalidEmoji',
  invalid_utf8: 'errors.api.invalidUtf8',
  project_load_failed: 'errors.api.projectLoadFailed',
  reaction_add_failed: 'errors.api.reactionAddFailed',
  reaction_remove_failed: 'errors.api.reactionRemoveFailed',

  // Workspaces / orgs (added 2026-04-23)
  handle_taken: 'errors.api.handleTaken',
  insufficient_permissions: 'errors.api.insufficientPermissions',
  invalid_handle: 'errors.api.invalidHandle',
  invalid_publish_min_role: 'errors.api.invalidPublishMinRole',
  invalid_role: 'errors.api.invalidRole',
  invalid_slug: 'errors.api.invalidSlug',
  invalid_visibility_setting: 'errors.api.invalidVisibilitySetting',
  invalid_website_access_setting: 'errors.api.invalidWebsiteAccessSetting',
  invite_accept_failed: 'errors.api.inviteAcceptFailed',
  invite_admin_only: 'errors.api.inviteAdminOnly',
  invite_cannot_resend: 'errors.api.inviteCannotResend',
  invite_create_failed: 'errors.api.inviteCreateFailed',
  invite_decline_failed: 'errors.api.inviteDeclineFailed',
  invite_load_failed: 'errors.api.inviteLoadFailed',
  invite_not_pending: 'errors.api.inviteNotPending',
  invite_process_failed: 'errors.api.inviteProcessFailed',
  invite_revoke_failed: 'errors.api.inviteRevokeFailed',
  invites_fetch_failed: 'errors.api.invitesFetchFailed',
  member_not_found: 'errors.api.memberNotFound',
  member_remove_failed: 'errors.api.memberRemoveFailed',
  member_update_failed: 'errors.api.memberUpdateFailed',
  members_fetch_failed: 'errors.api.membersFetchFailed',
  nothing_to_update: 'errors.api.nothingToUpdate',
  slug_check_failed: 'errors.api.slugCheckFailed',
  slug_in_use: 'errors.api.slugInUse',
  workspace_checkout_unavailable: 'errors.api.workspaceCheckoutUnavailable',
  workspace_create_failed: 'errors.api.workspaceCreateFailed',
  workspace_delete_failed: 'errors.api.workspaceDeleteFailed',
  workspace_fetch_failed: 'errors.api.workspaceFetchFailed',
  workspace_forbidden: 'errors.api.workspaceForbidden',
  workspace_load_failed: 'errors.api.workspaceLoadFailed',
  workspace_membership_check_failed: 'errors.api.workspaceMembershipCheckFailed',
  workspace_not_member: 'errors.api.workspaceNotMember',
  workspace_update_failed: 'errors.api.workspaceUpdateFailed',
  workspace_upgrade_business_required: 'errors.api.workspaceUpgradeBusinessRequired',
  workspace_upgrade_enterprise_required: 'errors.api.workspaceUpgradeEnterpriseRequired',
  workspace_upgrade_pro_required: 'errors.api.workspaceUpgradeProRequired',
  workspaces_fetch_failed: 'errors.api.workspacesFetchFailed',

  // Audit-finding-H5 — emitted by the SAML/SSO/SCIM/CMEK/audit-export
  // plan-gate middleware when a workspace's tier is below the required
  // tier for the requested route. `params.plan` carries the required
  // tier and `params.current` the current one so the upgrade CTA can
  // render "Upgrade from FREE to ENTERPRISE".
  plan_required: 'errors.api.planRequired',
  plan_lookup_failed: 'errors.api.planLookupFailed',
  invalid_tenant_id: 'errors.api.invalidTenantId',
  tenant_not_found: 'errors.api.tenantNotFound',

  // Server-emitted codes mapped 2026-05-08 (H30 audit fix). Each code resolves
  // to a key under errors.api.* in i18n/messages/<locale>/errors.json.
  ack_failed: 'errors.api.ackFailed',
  additional_credits_too_low: 'errors.api.additionalCreditsTooLow',
  ai_residency_invalid_mode: 'errors.api.aiResidencyInvalidMode',
  ai_residency_load_failed: 'errors.api.aiResidencyLoadFailed',
  ai_residency_save_failed: 'errors.api.aiResidencySaveFailed',
  already_attached: 'errors.api.alreadyAttached',
  already_configured: 'errors.api.alreadyConfigured',
  already_member: 'errors.api.alreadyMember',
  ambiguous_target: 'errors.api.ambiguousTarget',
  amount_invalid: 'errors.api.amountInvalid',
  amount_too_high: 'errors.api.amountTooHigh',
  amount_too_low: 'errors.api.amountTooLow',
  api_token_create_failed: 'errors.api.apiTokenCreateFailed',
  api_token_failed: 'errors.api.apiTokenFailed',
  api_token_not_found: 'errors.api.apiTokenNotFound',
  audit_log_load_failed: 'errors.api.auditLogLoadFailed',
  audit_log_read_failed: 'errors.api.auditLogReadFailed',
  authn_context_rejected: 'errors.api.authnContextRejected',
  billing_model_unavailable: 'errors.api.billingModelUnavailable',
  billing_model_update_failed: 'errors.api.billingModelUpdateFailed',
  cannot_disable_provisioning: 'errors.api.cannotDisableProvisioning',
  carryover_cap_negative: 'errors.api.carryoverCapNegative',
  carryover_cap_not_allowed: 'errors.api.carryoverCapNotAllowed',
  carryover_cap_required: 'errors.api.carryoverCapRequired',
  cert_not_found: 'errors.api.certNotFound',
  change_not_found: 'errors.api.changeNotFound',
  chat_concurrency_exhausted: 'errors.api.chatConcurrencyExhausted',
  cmek_disable_failed: 'errors.api.cmekDisableFailed',
  cmek_invalid_input: 'errors.api.cmekInvalidInput',
  cmek_kms_not_configured: 'errors.api.cmekKmsNotConfigured',
  cmek_roundtrip_failed: 'errors.api.cmekRoundtripFailed',
  cmek_status_load_failed: 'errors.api.cmekStatusLoadFailed',
  cmek_unsupported_provider: 'errors.api.cmekUnsupportedProvider',
  confirmation_mismatch: 'errors.api.confirmationMismatch',
  credit_pool_activate_failed: 'errors.api.creditPoolActivateFailed',
  credit_pool_already_active: 'errors.api.creditPoolAlreadyActive',
  credit_pool_caps_failed: 'errors.api.creditPoolCapsFailed',
  credit_pool_deactivate_failed: 'errors.api.creditPoolDeactivateFailed',
  credit_pool_not_active: 'errors.api.creditPoolNotActive',
  credit_pool_override_failed: 'errors.api.creditPoolOverrideFailed',
  credit_pool_read_failed: 'errors.api.creditPoolReadFailed',
  credit_pool_topup_failed: 'errors.api.creditPoolTopupFailed',
  credit_pool_topup_read_failed: 'errors.api.creditPoolTopupReadFailed',
  custom_domain_delete_failed: 'errors.api.customDomainDeleteFailed',
  custom_domain_disable_failed: 'errors.api.customDomainDisableFailed',
  custom_domain_list_failed: 'errors.api.customDomainListFailed',
  custom_domain_not_found: 'errors.api.customDomainNotFound',
  custom_domain_register_failed: 'errors.api.customDomainRegisterFailed',
  custom_domain_verify_failed: 'errors.api.customDomainVerifyFailed',
  domain_already_added: 'errors.api.domainAlreadyAdded',
  domain_not_found: 'errors.api.domainNotFound',
  dpia_generate_failed: 'errors.api.dpiaGenerateFailed',
  emit_failed: 'errors.api.emitFailed',
  empty_event_kinds: 'errors.api.emptyEventKinds',
  expires_in_invalid: 'errors.api.expiresInInvalid',
  expires_in_required: 'errors.api.expiresInRequired',
  expires_in_too_long: 'errors.api.expiresInTooLong',
  export_not_found: 'errors.api.exportNotFound',
  export_not_ready: 'errors.api.exportNotReady',
  export_url_missing: 'errors.api.exportUrlMissing',
  grant_negative: 'errors.api.grantNegative',
  hostname_taken: 'errors.api.hostnameTaken',
  invalid_billing_model: 'errors.api.invalidBillingModel',
  invalid_caps: 'errors.api.invalidCaps',
  invalid_carryover_policy: 'errors.api.invalidCarryoverPolicy',
  invalid_certificate: 'errors.api.invalidCertificate',
  invalid_change: 'errors.api.invalidChange',
  invalid_count: 'errors.api.invalidCount',
  invalid_direction: 'errors.api.invalidDirection',
  invalid_domain: 'errors.api.invalidDomain',
  invalid_effective_at: 'errors.api.invalidEffectiveAt',
  invalid_filter: 'errors.api.invalidFilter',
  invalid_format: 'errors.api.invalidFormat',
  invalid_from: 'errors.api.invalidFrom',
  invalid_hostname: 'errors.api.invalidHostname',
  invalid_idp_type: 'errors.api.invalidIdpType',
  invalid_key: 'errors.api.invalidKey',
  // invalid_org_id reuses the existing invalidOrg key.
  invalid_org_id: 'errors.api.invalidOrg',
  invalid_rule_kind: 'errors.api.invalidRuleKind',
  invalid_scope: 'errors.api.invalidScope',
  invalid_start_index: 'errors.api.invalidStartIndex',
  invalid_strategy: 'errors.api.invalidStrategy',
  invalid_to: 'errors.api.invalidTo',
  invalid_value: 'errors.api.invalidValue',
  invalid_webhook_url: 'errors.api.invalidWebhookUrl',
  invalid_workspace_id: 'errors.api.invalidWorkspaceId',
  last_owner: 'errors.api.lastOwner',
  list_failed: 'errors.api.listFailed',
  // missing_org_id maps to the existing invalidOrg key.
  missing_org_id: 'errors.api.invalidOrg',
  missing_path_param: 'errors.api.missingPathParam',
  model_pricing_delete_failed: 'errors.api.modelPricingDeleteFailed',
  model_pricing_load_failed: 'errors.api.modelPricingLoadFailed',
  model_pricing_not_found: 'errors.api.modelPricingNotFound',
  model_pricing_save_failed: 'errors.api.modelPricingSaveFailed',
  mtls_unavailable: 'errors.api.mtlsUnavailable',
  not_an_api_token: 'errors.api.notAnApiToken',
  not_cancelled: 'errors.api.notCancelled',
  not_enterprise_tier: 'errors.api.notEnterpriseTier',
  notification_kind_unknown: 'errors.api.notificationKindUnknown',
  notification_prefs_load_failed: 'errors.api.notificationPrefsLoadFailed',
  notification_prefs_reset_failed: 'errors.api.notificationPrefsResetFailed',
  notification_prefs_save_failed: 'errors.api.notificationPrefsSaveFailed',
  // not_org_member reuses the existing orgMembershipRequired key.
  not_org_member: 'errors.api.orgMembershipRequired',
  organization_billing_summary_failed: 'errors.api.organizationBillingSummaryFailed',
  organization_cancel_failed: 'errors.api.organizationCancelFailed',
  organization_create_failed: 'errors.api.organizationCreateFailed',
  organization_list_failed: 'errors.api.organizationListFailed',
  organization_load_failed: 'errors.api.organizationLoadFailed',
  organization_member_add_failed: 'errors.api.organizationMemberAddFailed',
  organization_member_remove_failed: 'errors.api.organizationMemberRemoveFailed',
  organization_members_failed: 'errors.api.organizationMembersFailed',
  organization_member_update_failed: 'errors.api.organizationMemberUpdateFailed',
  organization_not_found: 'errors.api.organizationNotFound',
  organization_restore_failed: 'errors.api.organizationRestoreFailed',
  organization_slug_rename_failed: 'errors.api.organizationSlugRenameFailed',
  organization_suspend_failed: 'errors.api.organizationSuspendFailed',
  organization_unsuspend_failed: 'errors.api.organizationUnsuspendFailed',
  organization_update_failed: 'errors.api.organizationUpdateFailed',
  organization_workspaces_failed: 'errors.api.organizationWorkspacesFailed',
  org_cancelled: 'errors.api.orgCancelled',
  org_has_no_owner: 'errors.api.orgHasNoOwner',
  // org_member_required mirrors the existing orgMembershipRequired key.
  org_member_required: 'errors.api.orgMembershipRequired',
  org_not_active: 'errors.api.orgNotActive',
  org_owner_required: 'errors.api.orgOwnerRequired',
  org_write_required: 'errors.api.orgWriteRequired',
  overage_price_negative: 'errors.api.overagePriceNegative',
  overage_price_not_allowed: 'errors.api.overagePriceNotAllowed',
  overage_price_required: 'errors.api.overagePriceRequired',
  override_not_found: 'errors.api.overrideNotFound',
  owner_only: 'errors.api.ownerOnly',
  period_already_rolled: 'errors.api.periodAlreadyRolled',
  period_window_missing: 'errors.api.periodWindowMissing',
  platform_admin_required: 'errors.api.platformAdminRequired',
  pricing_config_load_failed: 'errors.api.pricingConfigLoadFailed',
  pricing_config_save_failed: 'errors.api.pricingConfigSaveFailed',
  queue_failed: 'errors.api.queueFailed',
  reason_too_short: 'errors.api.reasonTooShort',
  retention_expired: 'errors.api.retentionExpired',
  rollover_failed: 'errors.api.rolloverFailed',
  rollover_unavailable: 'errors.api.rolloverUnavailable',
  rule_not_found: 'errors.api.ruleNotFound',
  same_org: 'errors.api.sameOrg',
  saml_replay_detected: 'errors.api.samlReplayDetected',
  scim_config_not_found: 'errors.api.scimConfigNotFound',
  scim_unavailable: 'errors.api.scimUnavailable',
  search_failed: 'errors.api.searchFailed',
  sla_get_failed: 'errors.api.slaGetFailed',
  // slug_invalid reuses the existing invalidSlug key.
  slug_invalid: 'errors.api.invalidSlug',
  // slug_taken reuses the existing slugInUse key.
  slug_taken: 'errors.api.slugInUse',
  sso_policy_save_failed: 'errors.api.ssoPolicySaveFailed',
  stripe_unavailable: 'errors.api.stripeUnavailable',
  system_setting_load_failed: 'errors.api.systemSettingLoadFailed',
  system_setting_save_failed: 'errors.api.systemSettingSaveFailed',
  // target_org_not_found is functionally identical to organizationNotFound.
  target_org_not_found: 'errors.api.organizationNotFound',
  topup_not_found: 'errors.api.topupNotFound',
  trust_center_get_failed: 'errors.api.trustCenterGetFailed',
  trust_center_save_failed: 'errors.api.trustCenterSaveFailed',
  txt_record_mismatch: 'errors.api.txtRecordMismatch',
  user_not_found: 'errors.api.userNotFound',
  verification_failed: 'errors.api.verificationFailed',
  waf_unavailable: 'errors.api.wafUnavailable',
  webhook_create_failed: 'errors.api.webhookCreateFailed',
  webhook_delete_failed: 'errors.api.webhookDeleteFailed',
  webhook_failed: 'errors.api.webhookFailed',
  webhook_not_found: 'errors.api.webhookNotFound',
  webhook_rotate_failed: 'errors.api.webhookRotateFailed',
  webhook_test_failed: 'errors.api.webhookTestFailed',
  webhook_update_failed: 'errors.api.webhookUpdateFailed',
  workspace_attach_failed: 'errors.api.workspaceAttachFailed',
  workspace_detach_failed: 'errors.api.workspaceDetachFailed',
  workspace_move_failed: 'errors.api.workspaceMoveFailed',
  workspace_not_in_organization: 'errors.api.workspaceNotInOrganization',
  workspace_plan_incompatible: 'errors.api.workspacePlanIncompatible',
};

/**
 * Map a backend error code to an i18n key.
 *
 * Returns the fallback key `errors.generic` for unknown codes, so callers can
 * unconditionally do `t(codeToI18nKey(code))` without guarding.
 */
export function codeToI18nKey(code: string | undefined | null): string {
  if (!code) return ERROR_CODE_FALLBACK;
  return ERROR_CODE_TO_I18N[code] ?? ERROR_CODE_FALLBACK;
}

/**
 * Does the given code have a specific i18n mapping (vs the generic fallback)?
 * Useful for call sites that want to fall back to a feature-specific default
 * translation (e.g. `auth.passkey.genericError`) when the code is unknown.
 */
export function hasMappedErrorCode(code: string | undefined | null): boolean {
  return !!code && code in ERROR_CODE_TO_I18N;
}
